import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { TrendingUp, Package, AlertTriangle, Truck, BellRing, MessageSquare, ArrowRight } from "lucide-react";
import DailyPnLCard from "../components/DailyPnLCard";
import WeeklyPnLCard from "../components/WeeklyPnLCard";
import LifetimePnLCard from "../components/LifetimePnLCard";
import CardEconomicsCard from "../components/CardEconomicsCard";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [queue, setQueue] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    // Each call runs independently — a failure in one no longer blanks
    // the whole dashboard. Errors surface in the red banner below.
    const collect = (label, err) => {
      const msg = err?.response?.data?.detail || err?.message || "unknown";
      const code = err?.response?.status || "?";
      setErrors((prev) => [...prev, `${label} → HTTP ${code} · ${msg}`]);
    };

    api.get("/admin/dashboard/stats")
      .then((r) => setStats(r.data))
      .catch((e) => { collect("stats", e); setStats({ revenue: 0, total_orders: 0, pending_orders: 0, same_day_queue: 0, fulfilled_orders: 0, out_of_stock_count: 0, notify_requests: 0, contact_messages: 0, reserved_pending: 0 }); });

    api.get("/admin/dashboard/same-day-queue")
      .then((r) => setQueue(r.data))
      .catch((e) => collect("same-day-queue", e));

    api.get("/admin/orders")
      .then((r) => setRecentOrders((r.data || []).slice(0, 6)))
      .catch((e) => collect("orders", e));
  }, []);

  if (!stats) return <div className="text-[#9CA8B8] pg-mono">Loading...</div>;

  const tiles = [
    { label: "Revenue (paid)", value: `£${stats.revenue.toFixed(2)}`, icon: TrendingUp, accent: "lime" },
    { label: "Total Orders", value: stats.total_orders, icon: Package },
    { label: "Pending", value: stats.pending_orders, icon: AlertTriangle },
    { label: "Same-Day Queue", value: stats.same_day_queue, icon: Truck, accent: "lime" },
    { label: "Fulfilled", value: stats.fulfilled_orders, icon: Package },
    { label: "Stock Reserved", value: stats.reserved_pending ?? 0, icon: AlertTriangle, accent: (stats.reserved_pending ?? 0) > 0 ? "lime" : undefined },
    { label: "Out of Stock", value: stats.out_of_stock_count, icon: AlertTriangle },
    { label: "Notify Requests", value: stats.notify_requests, icon: BellRing },
    { label: "Messages", value: stats.contact_messages, icon: MessageSquare },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="pg-micro mb-2">// Control Room</div>
      <h1 className="text-[#F7FAFC] text-3xl pg-display mb-8" style={{ fontFamily: "'Exo 2'" }}>
        Dashboard
      </h1>

      {errors.length > 0 && (
        <div className="mb-6 px-4 py-3 rounded-sm border border-[#EF4444]/45 bg-[#EF4444]/[0.06]" data-testid="admin-dashboard-errors">
          <div className="text-[#EF4444] pg-mono uppercase tracking-[0.18em] text-[10px] mb-2">
            // {errors.length} API call{errors.length === 1 ? "" : "s"} failed
          </div>
          <ul className="space-y-1 text-[12px] text-[#FCA5A5]">
            {errors.map((e, i) => (<li key={i} className="font-mono">{e}</li>))}
          </ul>
          <div className="mt-2 text-[11px] text-[#9CA8B8]">
            If this persists, share the messages above with David / the dev team.
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {tiles.map(({ label, value, icon: Icon, accent }) => (
          <div key={label} className="pg-card p-5" data-testid={`stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <Icon size={16} className={accent === "lime" ? "text-[#A6FF00]" : "text-[#00C2D6]"} />
            <div className="pg-micro mt-3 mb-1">{label}</div>
            <div className={`pg-display text-2xl ${accent === "lime" ? "text-[#A6FF00]" : "text-[#F7FAFC]"}`} style={{ fontFamily: "'Exo 2'" }}>
              {value}
            </div>
          </div>
        ))}
      </div>

      <LifetimePnLCard />
      <CardEconomicsCard />
      <DailyPnLCard />
      <WeeklyPnLCard />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="pg-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="pg-micro mb-1">// Same-Day Dispatch Queue</div>
              <h2 className="text-[#F7FAFC] pg-heading text-xl" style={{ fontFamily: "'Rajdhani'" }}>
                Pending eligible orders
              </h2>
            </div>
            <Link to="/admin/orders" className="text-xs pg-mono text-[#00C2D6] hover:text-[#A6FF00] uppercase tracking-[0.2em]">
              All orders <ArrowRight size={12} className="inline" />
            </Link>
          </div>
          {queue.length === 0 ? (
            <div className="text-[#9CA8B8] text-sm py-6 text-center">No orders in queue</div>
          ) : (
            <div className="space-y-2">
              {queue.map((o) => (
                <div key={o.id} className="flex items-center justify-between p-3 border border-[#A6FF00]/20 rounded-sm">
                  <div>
                    <div className="text-[#F7FAFC] pg-mono text-sm">{o.order_number}</div>
                    <div className="text-[#9CA8B8] text-xs">{o.shipping.full_name} · {o.shipping.city}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[#A6FF00] pg-mono text-sm">£{o.total.toFixed(2)}</div>
                    <div className="text-[#9CA8B8] text-xs uppercase tracking-[0.18em]">{o.status}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="pg-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="pg-micro mb-1">// Recent Orders</div>
              <h2 className="text-[#F7FAFC] pg-heading text-xl" style={{ fontFamily: "'Rajdhani'" }}>
                Latest activity
              </h2>
            </div>
          </div>
          {recentOrders.length === 0 ? (
            <div className="text-[#9CA8B8] text-sm py-6 text-center">No orders yet</div>
          ) : (
            <div className="space-y-2">
              {recentOrders.map((o) => (
                <Link key={o.id} to="/admin/orders" className="flex items-center justify-between p-3 border border-[#00C2D6]/20 rounded-sm hover:border-[#00C2D6]/50 transition-colors">
                  <div>
                    <div className="text-[#F7FAFC] pg-mono text-sm">{o.order_number}</div>
                    <div className="text-[#9CA8B8] text-xs">{o.items.length} item(s) · {o.shipping.city}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[#F7FAFC] pg-mono text-sm">£{o.total.toFixed(2)}</div>
                    <div className="text-[#9CA8B8] text-xs uppercase tracking-[0.18em]">{o.status}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
